import { SCROLL_AXES, alongAxis, crossAxisOf, pointOnAxis, type ScrollAxis, type ViewportPoint } from "./scroll-box-axes"
import { collectContentWithin, contentRects, isShownToUsers } from "./scroll-box-content-leaves"
import type { DetectedFinding, HiddenEndCause, HiddenEndFinding, ScrollEdge } from "./scroll-box-findings"
import { identifyScrollBox } from "./scroll-box-identity"
import {
  crossRectSpan,
  intersectSpans,
  rectSpan,
  scrollportSpan,
  shiftSpan,
  spanExtentPx,
  windowSpan,
  type PixelSpan,
} from "./scroll-box-spans"
import {
  OVERFLOW_THRESHOLD_PX,
  clipsOrScrollsAlong,
  hidesOwnContentAlong,
  isFixedPositioned,
  isLayered,
  isPageRoot,
  isStickyPositioned,
  overflowsAlong,
  pageClipsAlong,
  scrollsAlong,
  scrollsRightToLeft,
} from "./scroll-box-styles"

const SAMPLE_INSET_PX = 2
const EDGE_BAND_SHARE_OF_VISIBLE_EXTENT = 0.5
const TRANSIENT_NOTICE_SELECTOR = '[aria-live]:not([aria-live="off"]), [role="status"], [role="alert"]'

interface ContentTravel {
  towardStartPx: number
  towardEndPx: number
}

interface Scroller {
  owner: Element
  contentRoot: Element
  visible: PixelSpan
  visibleAcross: PixelSpan
  travel: ContentTravel
  travelAcross: ContentTravel
}

interface PlacedContent {
  along: PixelSpan
  across: PixelSpan
  visibleAcross: PixelSpan
  travelAcross: ContentTravel
}

const NO_TRAVEL: ContentTravel = { towardStartPx: 0, towardEndPx: 0 }

interface HiddenEdge {
  cause: HiddenEndCause
  hiddenPx: number
}

interface EdgeGeometry {
  edge: ScrollEdge
  shiftPx: number
}

function escapesIntoLayer(element: Element): boolean {
  return isFixedPositioned(element)
}

function sticksAlong(element: Element, axis: ScrollAxis): boolean {
  const style = getComputedStyle(element)
  return isStickyPositioned(element) && (style[axis.rectStart] !== "auto" || style[axis.rectEnd] !== "auto")
}

function addTravel(first: ContentTravel, second: ContentTravel): ContentTravel {
  return { towardStartPx: first.towardStartPx + second.towardStartPx, towardEndPx: first.towardEndPx + second.towardEndPx }
}

function placedRects(element: Element, rects: DOMRect[], scroller: Scroller, axis: ScrollAxis): PlacedContent[] {
  const crossAxis = crossAxisOf(axis)
  let visibleAcross = scroller.visibleAcross
  let travelAcross = scroller.travelAcross
  let isStuckAcross = false
  for (let ancestor: Element | null = element; ancestor && ancestor !== scroller.owner; ancestor = ancestor.parentElement) {
    if (sticksAlong(ancestor, crossAxis)) isStuckAcross = true
    if (clipsOrScrollsAlong(ancestor, crossAxis)) visibleAcross = intersectSpans(visibleAcross, scrollportSpan(ancestor, crossAxis))
    if (ancestor !== element && scrollsAlong(ancestor, crossAxis) && overflowsAlong(ancestor, crossAxis)) {
      travelAcross = addTravel(travelAcross, contentTravel(ancestor, crossAxis))
    }
  }
  return rects.map((rect) => ({
    along: rectSpan(rect, axis),
    across: crossRectSpan(rect, axis),
    visibleAcross,
    travelAcross: isStuckAcross ? NO_TRAVEL : travelAcross,
  }))
}

function edgeBandPx(scroller: Scroller): number {
  return spanExtentPx(scroller.visible) * EDGE_BAND_SHARE_OF_VISIBLE_EXTENT
}

function liesAwayFromBothEdges(scroller: Scroller, axis: ScrollAxis): (element: Element) => boolean {
  const endBandStartsAtPx = scroller.visible.endPx - edgeBandPx(scroller) + scroller.travel.towardStartPx
  const startBandEndsAtPx = scroller.visible.startPx + edgeBandPx(scroller) - scroller.travel.towardEndPx
  return (element) => {
    if (overflowsAlong(element, axis)) return false
    const along = rectSpan(element.getBoundingClientRect(), axis)
    return along.endPx < endBandStartsAtPx && along.startPx > startBandEndsAtPx
  }
}

function placedContentOf(scroller: Scroller, axis: ScrollAxis): PlacedContent[] {
  const content = collectContentWithin(scroller.contentRoot, axis, escapesIntoLayer, liesAwayFromBothEdges(scroller, axis))
  return [
    ...content.leaves.flatMap((leaf) => placedRects(leaf, contentRects(leaf), scroller, axis)),
    ...content.innerBoxes.flatMap((innerBox) => placedRects(innerBox, [innerBox.getBoundingClientRect()], scroller, axis)),
  ]
}

function pageScroller(): Element {
  return document.scrollingElement ?? document.documentElement
}

function documentScrollsAlong(axis: ScrollAxis): boolean {
  return !pageClipsAlong(axis) && overflowsAlong(pageScroller(), axis)
}

function contentTravel(scroller: Element, axis: ScrollAxis): ContentTravel {
  const maximumScrollPx = Math.max(0, scroller[axis.scrollSize] - scroller[axis.clientSize])
  const scrolledToPx = scroller[axis.scrollOffset]
  const rangeEndPx = scrollsRightToLeft(scroller, axis) ? 0 : maximumScrollPx
  const rangeStartPx = rangeEndPx - maximumScrollPx
  return { towardStartPx: Math.max(0, rangeEndPx - scrolledToPx), towardEndPx: Math.max(0, scrolledToPx - rangeStartPx) }
}

function isPinnedToWindow(box: Element, axis: ScrollAxis): boolean {
  if (isFixedPositioned(box)) return true
  for (let ancestor = box.parentElement; ancestor && !isPageRoot(ancestor); ancestor = ancestor.parentElement) {
    if (isFixedPositioned(ancestor)) return true
    if (scrollsAlong(ancestor, axis) && overflowsAlong(ancestor, axis)) return false
  }
  return !documentScrollsAlong(axis)
}

function visibleSpanOf(box: Element, axis: ScrollAxis): PixelSpan {
  let visible = scrollportSpan(box, axis)
  for (let ancestor = box.parentElement; ancestor && !isPageRoot(ancestor); ancestor = ancestor.parentElement) {
    if (clipsOrScrollsAlong(ancestor, axis)) visible = intersectSpans(visible, scrollportSpan(ancestor, axis))
    if (isFixedPositioned(ancestor)) break
  }
  return intersectSpans(visible, windowSpan(axis))
}

function isInsideLayerWithin(element: Element, owner: Element): boolean {
  for (let ancestor: Element | null = element; ancestor && ancestor !== owner; ancestor = ancestor.parentElement) {
    if (isFixedPositioned(ancestor)) return true
  }
  return false
}

function isOwnersContent(element: Element, owner: Element): boolean {
  return element === owner || element.contains(owner) || (owner.contains(element) && !isInsideLayerWithin(element, owner))
}

function coversWholeVisibleArea(element: Element, scroller: Scroller, axis: ScrollAxis): boolean {
  const rect = element.getBoundingClientRect()
  const along = rectSpan(rect, axis)
  const across = crossRectSpan(rect, axis)
  return (
    along.startPx <= scroller.visible.startPx &&
    along.endPx >= scroller.visible.endPx &&
    across.startPx <= scroller.visibleAcross.startPx &&
    across.endPx >= scroller.visibleAcross.endPx
  )
}

function layerOf(element: Element): Element {
  for (let ancestor: Element | null = element; ancestor && !isPageRoot(ancestor); ancestor = ancestor.parentElement) {
    if (isLayered(ancestor)) return ancestor
  }
  return element
}

function overlayAbove(scroller: Scroller, axis: ScrollAxis, point: ViewportPoint): Element | null {
  const stack = document.elementsFromPoint(point.x, point.y)
  const ownContentIndex = stack.findIndex((element) => isOwnersContent(element, scroller.owner))
  const above = ownContentIndex === -1 ? stack : stack.slice(0, ownContentIndex)
  if (above.length === 0) return null
  if (above.some((element) => coversWholeVisibleArea(element, scroller, axis))) return null
  return above[0].closest(TRANSIENT_NOTICE_SELECTOR) ? null : layerOf(above[0])
}

function samplePoints(placed: PlacedContent, axis: ScrollAxis, edge: ScrollEdge): ViewportPoint[] {
  const visibleAcross = intersectSpans(placed.across, placed.visibleAcross)
  if (spanExtentPx(visibleAcross) <= 0) return []
  const acrossPx = (visibleAcross.startPx + visibleAcross.endPx) / 2
  const centerPx = (placed.along.startPx + placed.along.endPx) / 2
  const edgePx = edge === "end" ? placed.along.endPx - SAMPLE_INSET_PX : placed.along.startPx + SAMPLE_INSET_PX
  return [centerPx, edgePx].map((alongPx) => pointOnAxis(axis, alongPx, acrossPx))
}

function isInsideSpan(valuePx: number, span: PixelSpan): boolean {
  return valuePx >= span.startPx && valuePx <= span.endPx
}

function escapesAcross(placed: PlacedContent, overlayAcross: PixelSpan): boolean {
  return (
    placed.across.endPx - placed.travelAcross.towardStartPx <= overlayAcross.startPx ||
    placed.across.startPx + placed.travelAcross.towardEndPx >= overlayAcross.endPx
  )
}

function coveredPxByOverlay(scroller: Scroller, axis: ScrollAxis, placed: PlacedContent, edge: ScrollEdge): number {
  const contentExtentPx = spanExtentPx(placed.along)
  for (const point of samplePoints(placed, axis, edge)) {
    if (!isInsideSpan(alongAxis(axis, point), scroller.visible)) continue
    const overlay = overlayAbove(scroller, axis, point)
    if (!overlay) continue
    const overlayRect = overlay.getBoundingClientRect()
    const overlaySpan = rectSpan(overlayRect, axis)
    const roomBeyondOverlayPx =
      edge === "end" ? scroller.visible.endPx - overlaySpan.endPx : overlaySpan.startPx - scroller.visible.startPx
    if (roomBeyondOverlayPx >= contentExtentPx) continue
    if (escapesAcross(placed, crossRectSpan(overlayRect, axis))) continue
    return edge === "end"
      ? Math.min(contentExtentPx, placed.along.endPx - overlaySpan.startPx)
      : Math.min(contentExtentPx, overlaySpan.endPx - placed.along.startPx)
  }
  return 0
}

function isInEdgeBand(along: PixelSpan, visible: PixelSpan, edge: ScrollEdge): boolean {
  const bandPx = spanExtentPx(visible) * EDGE_BAND_SHARE_OF_VISIBLE_EXTENT
  return edge === "end"
    ? along.endPx >= visible.endPx - bandPx && along.startPx >= visible.startPx
    : along.startPx <= visible.startPx + bandPx && along.endPx <= visible.endPx
}

function pastVisibleEdgePx(along: PixelSpan, visible: PixelSpan, edge: ScrollEdge): number {
  return edge === "end" ? along.endPx - visible.endPx : visible.startPx - along.startPx
}

function hiddenEdge(scroller: Scroller, axis: ScrollAxis, content: PlacedContent[], geometry: EdgeGeometry): HiddenEdge | null {
  const projected = content.map((placed) => ({ ...placed, along: shiftSpan(placed.along, geometry.shiftPx) }))
  const pastWindowPx = Math.max(0, ...projected.map((placed) => pastVisibleEdgePx(placed.along, scroller.visible, geometry.edge)))
  if (pastWindowPx > OVERFLOW_THRESHOLD_PX) return { cause: "window-or-clip", hiddenPx: pastWindowPx }
  const coveredPx = Math.max(
    0,
    ...projected
      .filter((placed) => isInEdgeBand(placed.along, scroller.visible, geometry.edge))
      .map((placed) => coveredPxByOverlay(scroller, axis, placed, geometry.edge)),
  )
  return coveredPx > OVERFLOW_THRESHOLD_PX ? { cause: "overlay", hiddenPx: coveredPx } : null
}

function hiddenEndFindings(scroller: Scroller, axis: ScrollAxis): HiddenEndFinding[] {
  const content = placedContentOf(scroller, axis)
  if (content.length === 0) return []
  const geometries: EdgeGeometry[] = [
    { edge: "end", shiftPx: -scroller.travel.towardStartPx },
    { edge: "start", shiftPx: scroller.travel.towardEndPx },
  ]
  return geometries.flatMap((geometry) => {
    const hidden = hiddenEdge(scroller, axis, content, geometry)
    if (!hidden) return []
    const finding: HiddenEndFinding = {
      kind: "hidden-end",
      identity: identifyScrollBox(scroller.owner),
      boxWidthPx: scroller.owner.clientWidth,
      boxHeightPx: scroller.owner.clientHeight,
      axis: axis.name,
      edge: geometry.edge,
      cause: hidden.cause,
      hiddenPx: hidden.hiddenPx,
    }
    return [finding]
  })
}

function hidesEndsAlong(box: Element, axis: ScrollAxis): boolean {
  return hidesOwnContentAlong(box, axis) && isPinnedToWindow(box, axis)
}

export function detectHiddenEnds(box: Element): HiddenEndFinding[] {
  if (isPageRoot(box) || !isShownToUsers(box)) return []
  return SCROLL_AXES.filter((axis) => hidesEndsAlong(box, axis)).flatMap((axis) =>
    hiddenEndFindings(
      {
        owner: box,
        contentRoot: box,
        visible: visibleSpanOf(box, axis),
        visibleAcross: visibleSpanOf(box, crossAxisOf(axis)),
        travel: contentTravel(box, axis),
        travelAcross: contentTravel(box, crossAxisOf(axis)),
      },
      axis,
    ),
  )
}

export function detectPageHiddenEnds(): DetectedFinding[] {
  return SCROLL_AXES.filter(documentScrollsAlong).flatMap((axis) =>
    hiddenEndFindings(
      {
        owner: document.documentElement,
        contentRoot: document.body,
        visible: windowSpan(axis),
        visibleAcross: windowSpan(crossAxisOf(axis)),
        travel: contentTravel(pageScroller(), axis),
        travelAcross: contentTravel(pageScroller(), crossAxisOf(axis)),
      },
      axis,
    ).map((finding) => ({ element: document.body, finding })),
  )
}
